import { Button, FlexContainer } from '@act/storybook';
import { Box, useTheme } from '@mui/material';
import { useState, useMemo } from 'react';
import { UserDto, SurveyV2 } from '@act/shared/data-transfer-objects';
import { goalsTrackingSelectors } from 'redux/slices/goalsTracking.slice';
import StyledAlert from 'toolkit/alerts/styled-alert';
import Goals from './Goals';

interface GoalsContentProps {
  selectedMember: UserDto;
}

const GoalsContent = ({ selectedMember }: GoalsContentProps) => {
  const theme = useTheme();
  const [view, setView] = useState<'inProgress' | 'completed'>('inProgress');

  const aggregatedGoals =
    goalsTrackingSelectors.useSelectAggregatedGoalsForMember(
      selectedMember?.id
    );

  // Count incomplete hospitalization surveys
  const hospitalizationSurveysCount = useMemo(() => {
    if (!aggregatedGoals?.[view]) return 0;
    return aggregatedGoals[view].filter(goal => {
      if (goal.goalType !== 'survey' || goal.completed) return false;
      const survey = goal as SurveyV2.AssignedSurveyDto;
      return survey.hospitalizations && survey.hospitalizations.length > 0;
    }).length;
  }, [aggregatedGoals, view]);

  return (
    <FlexContainer
      direction="column"
      height="100%"
      width="100%"
      padding="0 12px"
      backgroundColor={theme.palette.surfaces.neutralLighter}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            padding: '16px 0 0',
            width: '100%',
            boxSizing: 'border-box',
            backgroundColor: `${theme.palette.greyShades[100]}`,
          }}
        >
          <Button
            color="primary"
            variant={view === 'inProgress' ? 'contained' : 'outlined'}
            onClick={() => setView('inProgress')}
          >
            To do
          </Button>
          <Button
            color="primary"
            variant={view === 'completed' ? 'contained' : 'outlined'}
            onClick={() => setView('completed')}
          >
            Done
          </Button>
        </Box>
        {hospitalizationSurveysCount > 1 && (
          <Box
            sx={{
              padding: '8px 0 0',
              '& .MuiAlert-root': {
                padding: '4px 12px',
                minHeight: 'auto',
              },
              '& .MuiAlert-message': {
                padding: '4px 0',
              },
              '& .MuiAlert-icon': {
                padding: '4px 0',
                marginRight: '8px',
              },
            }}
          >
            <StyledAlert
              severity="info"
              variant="tonal"
              mode="inline"
              message="Your member has multiple hospitalization surveys as a result of multiple visits."
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        <Goals selectedMember={selectedMember} view={view} />
      </Box>
    </FlexContainer>
  );
};

export default GoalsContent;
